import React, { useEffect, useRef, useState } from 'react';
import { PublishAccountState } from '../hooks/usePublishAccount';
import './PublishAccountChip.css';

/** Shows the account a post will be published to; click to switch. Renders nothing without accounts. */
const PublishAccountChip: React.FC<{ pa: PublishAccountState }> = ({ pa }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (pa.loading || pa.accounts.length === 0) return null;

  const label = pa.selected ? pa.selected.name : 'Choose account';
  const offDefault = pa.selected && pa.defaultId && pa.selected.id !== pa.defaultId;

  return (
    <div className="pac-root" ref={rootRef}>
      <button
        type="button"
        className={`btn-secondary pac-chip${pa.needsChoice ? ' pac-chip--warn' : ''}`}
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={pa.selected ? `Publishing to ${pa.selected.name}` : 'Choose the account to publish to'}
      >
        {pa.selected?.avatarUrl
          ? <img className="pac-avatar" src={pa.selected.avatarUrl} alt="" />
          : <span className="pac-avatar pac-avatar--fallback" aria-hidden="true">{label.charAt(0).toUpperCase()}</span>}
        <span className="pac-label">{label}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {offDefault && <span className="pac-note">Not the project default</span>}
      {open && (
        <div className="pac-menu" role="listbox" aria-label="Publishing account">
          {pa.accounts.map(acc => (
            <button
              type="button"
              key={acc.id}
              role="option"
              aria-selected={acc.id === pa.selectedId}
              className={`pac-option${acc.id === pa.selectedId ? ' pac-option--active' : ''}`}
              onClick={() => { pa.setSelectedId(acc.id); setOpen(false); }}
            >
              <span className="pac-option-main">
                <span className="pac-option-name">{acc.name}</span>
                {acc.email && <span className="pac-option-email">{acc.email}</span>}
              </span>
              <span className="pac-option-tags">
                {acc.id === pa.defaultId && <span className="pac-tag">Default</span>}
                {acc.isTenant && <span className="pac-tag">Tenant</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PublishAccountChip;
